import { FC, Fragment, useCallback, useContext, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { getIcon, getPayment, getStatus, moSortOptions } from 'constants/mo';
import { MoTableData, MyOrder } from 'ts/shared/interfaces/MOResponse';

import { faFile, faCopy, faInfo, faTimes, faUser, faTicketSimple, faInfoCircle, faHotel, faCalendar, faTicket, faSearch, faPhoneSquare, faCheckSquare, faExclamationCircle, faLock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatPriceCurrency, getCurrency, showSuccess } from 'utils/utils';
import Button from 'components/ui/Button/Button';
import { Select } from 'ts/interfaces/Generic';
import Tooltip from 'components/ui/Tooltip/Tooltip';
import MoCancelModal from './MoActions/MoCancelModal';
import MoMoreDetailsModal from './MoActions/MoMoreDetailsModal';
import MoStatus from './MoActions/MoStatus';
import MoCog from './MoActions/MoCog';
import MoChangeRequest from './MoActions/MoChangeRequest';
import MoDocumentsModal from './MoActions/MoDocumentModal';
import withErrorBoundary from '../withErrorBoundary';
import { dateFormat, matchesMainFilter } from 'utils/shared/utils';
import MoStatistics from './MoStatistics';
import { MoContext } from 'contexts/MoContext';
import InfiniteScroll from 'react-infinite-scroller';
import { useTranslation } from 'react-i18next';
import { matchesSearchFilters, SearchFilters } from 'utils/shared/my-order';
import SortBy from '../SortBy';
import { useClickAway } from 'hooks/useClickAway';
import { getOrdersListFilter, getOrdersListFilterVersion, subscribeOrdersListFilter } from './Assistant/ordersActions';
import ReactCountryFlag from 'react-country-flag';
import { faCcVisa } from '@fortawesome/free-brands-svg-icons';
import { MainContext } from 'contexts/MainContext';

interface MoMode2Props {
  onUpdateMyOrder: (row: MyOrder) => void;
  myOrders: MyOrder[];
  tableData: MoTableData[];
}

const MoMode2: FC<MoMode2Props> = ({ ...props }) => {
  const { user } = useContext(MainContext);
  const { mainFilter, searchModel, setVisibleRows, activeOrder, setActiveOrder } = useContext(MoContext);
  const [t] = useTranslation();
  const [selectedOrder, setSelectedOrder] = useState<MyOrder | null>(null);
  const [isDocumentsModalOpen, setIsDocumentsModalOpen] = useState(false);
  const [isMoreDetailsModalOpen, setIsMoreDetailsModalOpen] = useState(false);
  const [moreDetailsOrder, setMoreDetailsOrder] = useState<MyOrder | null>(null);
  const [activeTab, setActiveTab] = useState<number>();
  const count = 20;
  const [total, setTotal] = useState(count);
  const [hasMore, setHasMore] = useState(true);
  const [visibleMyOrders, setVisibleMyOrders] = useState<any[]>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const listFilterVersion = useSyncExternalStore(subscribeOrdersListFilter, getOrdersListFilterVersion);
  const [debouncedGlobalFilter, setDebouncedGlobalFilter] = useState('');
  const [sortValue, setSortValue] = useState<string>('');
  const [hoveredOrderId, setHoveredOrderId] = useState<number | null>(null);
  const [hotelInfoOrderId, setHotelInfoOrderId] = useState<number | null>(null);
  const sortOptions: Select[] = useMemo(() => moSortOptions, []);
  const isMobile = window.innerWidth < 768;
  const dropdownRef = useClickAway(() => {
    setHoveredOrderId(null);
  });
  const hotelInfoDropdownRef = useClickAway(() => {
    setHotelInfoOrderId(null);
  });

  const sortOrders = useCallback((orders: any[], sortType: string): any[] => {
    if (!sortType) return orders;

    const sorted = [...orders].sort((a, b) => {
      switch (sortType) {
        case 'priceIncrease':
          return (a.orderRow.price || 0) - (b.orderRow.price || 0);
        case 'priceDecrease':
          return (b.orderRow.price || 0) - (a.orderRow.price || 0);
        case 'creationDateIncrease':
          return new Date(a.orderRow.dateC).getTime() - new Date(b.orderRow.dateC).getTime();
        case 'creationDateDecrease':
          return new Date(b.orderRow.dateC).getTime() - new Date(a.orderRow.dateC).getTime();
        case 'checkInIncrease':
          return new Date(a.fullSegment.checkIn).getTime() - new Date(b.fullSegment.checkIn).getTime();
        case 'checkInDecrease':
          return new Date(b.fullSegment.checkIn).getTime() - new Date(a.fullSegment.checkIn).getTime();
        case 'CXLDateIncrease': {
          const dateA = a.fullSegment.rooms?.[0]?.cancellationDateLimit;
          const dateB = b.fullSegment.rooms?.[0]?.cancellationDateLimit;
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return new Date(dateA).getTime() - new Date(dateB).getTime();
        }
        case 'CXLDateDecrease': {
          const dateA = a.fullSegment.rooms?.[0]?.cancellationDateLimit;
          const dateB = b.fullSegment.rooms?.[0]?.cancellationDateLimit;
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return new Date(dateB).getTime() - new Date(dateA).getTime();
        }
        case 'segmentIdIncrease':
          return (Number(a.fullSegment.orderSegId) || 0) - (Number(b.fullSegment.orderSegId) || 0);
        case 'segmentIdDecrease':
          return (Number(b.fullSegment.orderSegId) || 0) - (Number(a.fullSegment.orderSegId) || 0);
        case 'itemNameIncrease':
          return (a.fullSegment.itemDesc || '').localeCompare(b.fullSegment.itemDesc || '');
        case 'itemNameDecrease':
          return (b.fullSegment.itemDesc || '').localeCompare(a.fullSegment.itemDesc || '');
        default:
          return 0;
      }
    });

    return sorted;
  }, []);

  const [debouncedSearchFilters, setDebouncedSearchFilters] = useState<SearchFilters>({});

  useEffect(() => {
    if (searchModel) {
      const filters: SearchFilters = {
        orderSupplierPNR: searchModel.orderSupplierPNR,
        orderBackOfficeId: searchModel.orderBackOfficeId,
        orderPaxName: searchModel.orderPaxName,
        orderItem: searchModel.orderItem,
      };

      const timer = setTimeout(() => {
        setDebouncedSearchFilters(filters);
      }, 300);

      return () => clearTimeout(timer);
    } else {
      setDebouncedSearchFilters({});
    }
  }, [searchModel]);

  useEffect(() => {
    const update = getOrdersListFilter();
    if (!listFilterVersion) return;
    if (update.clearText) setGlobalFilter(update.text ?? '');
    else if (update.text !== undefined) setGlobalFilter(update.text);
  }, [listFilterVersion]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedGlobalFilter(globalFilter);
    }, 300);

    return () => clearTimeout(timer);
  }, [globalFilter]);

  const matchesGlobalFilter = useMemo(() => {
    return (order: MyOrder): boolean => {
      if (!debouncedGlobalFilter.trim()) return true;

      const searchTerm = debouncedGlobalFilter.toLowerCase().trim();

      const toSearchableString = (value: any): string => {
        if (value === null || value === undefined) return '';
        if (typeof value === 'string') return value.toLowerCase();
        if (typeof value === 'number') return value.toString();
        if (typeof value === 'boolean') return value ? 'true' : 'false';
        return String(value).toLowerCase();
      };

      if (toSearchableString(order.orderRow.orderId).includes(searchTerm)) return true;

      if (toSearchableString(order.fullSegment.orderSegId).includes(searchTerm)) return true;

      if (toSearchableString(order.orderRow.status).includes(searchTerm)) return true;

      if (order.orderRow.voucher && 'voucher'.includes(searchTerm)) return true;

      if (toSearchableString(order.fullSegment.sysSuppDesc).includes(searchTerm)) return true;
      if (toSearchableString(order.fullSegment.sysSuppCode).includes(searchTerm)) return true;

      if (order.fullSegment.suppPnr && toSearchableString(order.fullSegment.suppPnr).includes(searchTerm)) return true;

      if (order.order.paxes?.[0]?.fullDisplayName && toSearchableString(order.order.paxes[0].fullDisplayName).includes(searchTerm)) return true;

      const creationDate = dateFormat(order.orderRow.dateC, 'dd/MM/yy');
      if (toSearchableString(creationDate).includes(searchTerm)) return true;

      const checkInDate = dateFormat(order.fullSegment.checkIn, 'dd/MM/yy');
      if (toSearchableString(checkInDate).includes(searchTerm)) return true;

      if (toSearchableString(order.fullSegment.productType).includes(searchTerm)) return true;

      if (order.fullSegment.itemDesc && toSearchableString(order.fullSegment.itemDesc).includes(searchTerm)) return true;

      if (order.fullSegment.rooms) {
        for (const room of order.fullSegment.rooms) {
          if (room.roomDisplay && toSearchableString(room.roomDisplay).includes(searchTerm)) return true;
          if (room.paxes) {
            for (const pax of room.paxes) {
              if (pax.fullDisplayName && toSearchableString(pax.fullDisplayName).includes(searchTerm)) return true;
            }
          }
          if (room.sysStatus?.code && toSearchableString(room.sysStatus.code).includes(searchTerm)) return true;
        }
      }

      const formattedPrice = formatPriceCurrency(order.orderRow.price, getCurrency(order.orderRow.currency));
      if (toSearchableString(formattedPrice).includes(searchTerm)) return true;
      if (toSearchableString(order.orderRow.price).includes(searchTerm)) return true;
      if (toSearchableString(order.orderRow.currency).includes(searchTerm)) return true;

      return false;
    };
  }, [debouncedGlobalFilter]);

  useEffect(() => {
    const filtered = props.myOrders
      .filter((order) => {
        const isVisibleInTable = props.tableData.some((item) => item.segmentId === order.fullSegment.orderSegId && item.visible);
        if (!isVisibleInTable) return false;

        const clx = dateFormat(order.orderRow.roomsFirstCXL, 'dd/MM/yy');
        if (!matchesMainFilter(order.orderRow, clx, mainFilter)) return false;

        if (!matchesGlobalFilter(order)) return false;

        if (!matchesSearchFilters(order, debouncedSearchFilters)) return false;

        return true;
      })
      .map((order) => {
        const tableDataItem = props.tableData.find((item) => item.segmentId === order.fullSegment.orderSegId);
        return {
          ...order,
          segmentColor: tableDataItem?.segmentColor || '',
        };
      });

    const sorted = sortOrders(filtered, sortValue);
    setVisibleMyOrders(sorted);
    setVisibleRows(sorted.map((item) => item.fullSegment.orderSegId));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.tableData, props.myOrders, mainFilter, matchesGlobalFilter, debouncedSearchFilters, sortValue]);

  useEffect(() => {
    if (!activeOrder) return;
    sessionStorage.setItem('activeMyOrderNames', JSON.stringify(activeOrder.order.paxes));
  }, [activeOrder]);

  const handleDocumentsClick = (order: MyOrder) => {
    setSelectedOrder(order);
    setIsDocumentsModalOpen(true);
  };

  const handleMoreDetailsClick = (order: MyOrder) => {
    setMoreDetailsOrder(order);
    setIsMoreDetailsModalOpen(true);
    setActiveTab(undefined);
  };

  const handleUpdateMyOrder = (updatedOrder: MyOrder) => {
    props.onUpdateMyOrder(updatedOrder);
    if (selectedOrder && selectedOrder.orderRow.orderId === updatedOrder.orderRow.orderId) {
      setSelectedOrder(updatedOrder);
    }
    if (moreDetailsOrder && moreDetailsOrder.orderRow.orderId === updatedOrder.orderRow.orderId) {
      setMoreDetailsOrder(updatedOrder);
    }
  };

  const handlePriceDetailsClick = (order: MyOrder) => {
    setMoreDetailsOrder(order);
    setActiveTab(6);
    setIsMoreDetailsModalOpen(true);
  };

  const handleCxlClick = (order: MyOrder) => {
    setMoreDetailsOrder(order);
    setActiveTab(8);
    setIsMoreDetailsModalOpen(true);
  };

  const handleCopyPhone = (phone: string) => {
    navigator.clipboard.writeText(phone);
    showSuccess('Phone copied to clipboard!');
  };

  useEffect(() => {
    setHasMore(visibleMyOrders.length > count);
    setTotal(count);
  }, [visibleMyOrders?.length]);

  const fetchData = () => {
    setTotal((prevTotal) => {
      const newTotal = prevTotal + count;
      setHasMore(newTotal < visibleMyOrders.length);
      return newTotal;
    });
  };

  const onClickSearchMobile = () => {
    const el = document.querySelector('.form-wrapper.m-search') as HTMLElement;
    if (el) {
      const currentDisplay = window.getComputedStyle(el).display;
      el.style.display = currentDisplay === 'none' ? 'flex' : 'none';
    }
  };

  const handlePaymentDetailsClick = (order: MyOrder) => {
    setMoreDetailsOrder(order);
    setActiveTab(3);
    setIsMoreDetailsModalOpen(true);
  };

  const handleRemarksClick = (order: MyOrder) => {
    setMoreDetailsOrder(order);
    setActiveTab(0);
    setIsMoreDetailsModalOpen(true);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    showSuccess('Copied to clipboard!');
  };

  const visibleOrders = visibleMyOrders.slice(0, total);

  const onChangeActiveOrderByDirection = useCallback((direction: 1 | -1) => {
    if (!visibleOrders.length) return false;

    const currentIndex = visibleOrders.findIndex(
      (order) => order.fullSegment.orderSegId === activeOrder?.fullSegment.orderSegId
    );

    const nextIndex = currentIndex === -1
      ? direction === 1
        ? 0
        : visibleOrders.length - 1
      : currentIndex + direction;

    if (nextIndex < 0 || nextIndex >= visibleOrders.length) return false;

    const nextOrder = visibleOrders[nextIndex];
    if (!nextOrder) return false;

    setActiveOrder(nextOrder);
    return true;
  }, [activeOrder?.fullSegment.orderSegId, visibleOrders, setActiveOrder]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

      const target = event.target as HTMLElement | null;
      if (target) {
        const tagName = target.tagName.toLowerCase();
        if (tagName === 'input' || tagName === 'textarea' || tagName === 'select' || target.isContentEditable) {
          return;
        }
      }

      const isChanged = event.key === 'ArrowDown'
        ? onChangeActiveOrderByDirection(1)
        : onChangeActiveOrderByDirection(-1);

      if (isChanged) {
        event.preventDefault();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onChangeActiveOrderByDirection]);

  useEffect(() => {
    if (!activeOrder) return;

    const activeElement = document.querySelector('.mo-orders-v2 .details-block.active') as HTMLElement | null;
    if (!activeElement) return;

    activeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrder?.fullSegment.orderSegId, visibleOrders.length]);

  return (
    <Fragment>
      <div data-assistant-target="list">
        {props.tableData.length > 0 && (
          <div className='mb-5 mt-5 mo-status-2 ph-10 flex-row'>
            <MoStatistics tableData={props.tableData} />

            <div className="ph-5 cur-poi m-search-mobile" onClick={onClickSearchMobile}>
              <FontAwesomeIcon icon={faSearch} />
            </div>

            <div className='ph-10'>
              <div className='position-relative' style={{ width: '250px' }}>
                <input
                  className="mo-table-filter-input"
                  placeholder="Filter..."
                  value={globalFilter}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  style={{ width: '100%', height: 30, fontSize: '1rem', margin: '-3px 0' }}
                />
                {globalFilter && (
                  <span
                    className="cur-poi"
                    onClick={() => setGlobalFilter('')}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: 4,
                      color: '#999',
                    }}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </span>
                )}
              </div>
            </div>
            <div className='mo-status-sort'>
              <SortBy
                onSortChange={setSortValue}
                options={sortOptions}
                newSort={sortValue}
              />
            </div>
          </div>
        )}
        <div className='mo-orders-v2'>
          <InfiniteScroll
            loadMore={fetchData}
            hasMore={hasMore}
            useWindow={false}
            loader={<div key={0}></div>}
          >
            {visibleOrders.map((order) => {
              const isActiveOrder = activeOrder?.fullSegment.orderSegId === order.fullSegment.orderSegId;

              return (
                <div key={`${order.orderRow.orderId}-${order.fullSegment.orderSegId}`} className={`default-shadow details-block details-block-p15${isActiveOrder ? ' active' : ''}`} onClick={() => setActiveOrder(order)}>
                  <div className="title title-header-grey">
                    <div className='flex-between'>
                      <div>
                        <span className="ph-5 text-sm us-none">
                          <FontAwesomeIcon icon={getIcon(order.fullSegment.productType) as any} size='sm' className='mr-5' />

                          <span className="ph-5 text-sm cur-poi" onDoubleClick={() => copyToClipboard(order.orderRow.orderId)}>
                            <span>Order: </span>
                            <span className="text-contrast text-st">{order.orderRow.orderId}</span>

                            <span className='cur-poi ph-5 text-sm' style={{ marginInlineEnd: 5 }} onClick={() => copyToClipboard(`${order.orderRow.orderId}`)}>
                              <FontAwesomeIcon icon={faCopy} size='xs' />
                            </span>
                          </span>
                          <span className='cur-poi' onDoubleClick={() => copyToClipboard(order.fullSegment.orderSegId)}>
                            {order.order.segments?.length > 1 && <span>({order.order.segments?.length}) </span>}
                            Segment:{' '}
                            <span className="text-contrast text-st" style={{ backgroundColor: order.segmentColor }}>{order.fullSegment.orderSegId}</span>
                          </span>
                          <span className='cur-poi ph-5 text-sm' onClick={() => copyToClipboard(`${order.fullSegment.orderSegId}`)}>
                            <FontAwesomeIcon icon={faCopy} size='xs' />
                          </span>
                        </span>

                        <span className="ph-5 text-sm">
                          <span className={`mo-status ${getStatus(order.orderRow.status)}`}>
                            {order.orderRow.status}
                          </span>
                        </span>

                        {order.orderRow.voucher && (
                          <span className="text-sm">
                            <span className="mo-status mo-status-success">VOUCHER</span>
                          </span>
                        )}

                      </div>
                      <Button
                        icon={faFile}
                        title={`Documents (${order.order.documents.length})`}
                        iconclassname='mr-5'
                        className='btn btn-link btn-sm ph-0'
                        onClick={() => handleDocumentsClick(order)}
                      />
                    </div>
                  </div>
                  <div className='row mo-card-row bb-1 mb-10 pb-5'>
                    <div className='col-md-31'>
                      <div className='text-lg display-flex'>
                        <span className='mw-92 display-ib cur-poi us-none' onDoubleClick={() => copyToClipboard(order.fullSegment.suppPnr)}>
                          <span className='display-ib text-st' style={{ width: 23 }}><FontAwesomeIcon icon={faTicketSimple} /></span>
                          PNR:</span>
                        {!!order.fullSegment.suppPnr && (
                          <span className='ph-5 mo-pnr cur-poi us-none' onDoubleClick={() => copyToClipboard(order.fullSegment.suppPnr.replace('BPRLOCK:', ''))}>
                            {order.fullSegment.suppPnr.includes('BPRLOCK:') && <FontAwesomeIcon icon={faLock} size='xs' className='mr-5' />}
                            {order.fullSegment.suppPnr.replace('BPRLOCK:', '')}</span>
                        )}
                      </div>
                    </div>
                    <div className='col-md-38'>
                      <div className='w100p flex-row' style={{ alignItems: 'center' }}>
                        <FontAwesomeIcon
                          icon={getIcon(order.fullSegment.productType)}
                          size="sm"
                        />
                        <span className='ph-10 text-limited text-bold text-lg cur-poi us-none' style={{ maxWidth: 'calc(100% - 42px)' }} onDoubleClick={() => copyToClipboard(order.fullSegment.itemDesc)}>
                          {order.fullSegment.itemDesc}
                        </span>
                        {order.fullSegment.productType === 'HTL' && (
                          <span
                            className='display-ib ph-5 position-relative'
                          >
                            <FontAwesomeIcon icon={faInfoCircle as any} size='sm'
                              className='cur-poi'
                              ref={hotelInfoDropdownRef}
                              onClick={() => setHotelInfoOrderId(hotelInfoOrderId === order.fullSegment.orderSegId ? null : order.fullSegment.orderSegId)} />
                            <div className={'header-dropdown pb-10' + (hotelInfoOrderId === order.fullSegment.orderSegId ? ' open' : '')} style={{ left: 0, right: -300, top: 25 }}>
                              <div className="ph-10">
                                <div className='text-bold mt-10 ph-10 bb-1 mb-5'>{order.fullSegment.itemDesc}</div>
                                <div className="ph-10 text-sm display-flex">
                                  <span className='text-normal mw-70'>
                                    Address:</span>
                                  {order.fullSegment.itemAddress && (
                                    <span className='text-bold'>{order.fullSegment.itemAddress}</span>
                                  )}
                                </div>
                                {order.fullSegment.itemPhone && (
                                  <div className="ph-10 text-sm display-flex">
                                    <span className="mw-70">Phone:</span>

                                    <span className='text-bold flex-v-center flex-gap-5'>
                                      {order.fullSegment.itemPhone}
                                      <FontAwesomeIcon
                                        icon={faCopy}
                                        className="cur-poi"
                                        onClick={() => handleCopyPhone(order.fullSegment.itemPhone)}
                                        size="xs"
                                      />
                                    </span>
                                  </div>
                                )}
                                {order.fullSegment.itemZip && (
                                  <div className="ph-10 text-sm display-flex">
                                    <span className="mw-70">Zip:</span>
                                    <span className='text-bold'>{order.fullSegment.itemZip}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </span>
                        )}

                      </div>
                    </div>
                    <div className='col-md-31 text-right'>

                      <div className='text-lg'>Total Price:  {formatPriceCurrency(order.orderRow.price, getCurrency(order.orderRow.currency))}</div>
                    </div>
                  </div>
                  <div className='row mo-card-row'>
                    <div className='col-md-31 br-1'>
                      {user?.branch?.hotelDisplaySupplierName && (
                        <div className='cur-poi us-none' onDoubleClick={() => copyToClipboard(order.fullSegment.sysSuppDesc)}>
                          <span className='mw-92 display-ib'><span className='display-ib' style={{ width: 23 }}><FontAwesomeIcon icon={faHotel} /></span>Supplier:</span>
                          <span className='text-bold ph-5'>{order.fullSegment.sysSuppDesc} ({order.fullSegment.sysSuppCode})</span>
                        </div>
                      )}
                      <div className='cur-poi us-none' onDoubleClick={() => copyToClipboard(order.order.paxes?.[0].fullDisplayName)}>
                        <span className='mw-92 display-ib'>
                          <span className='display-ib' style={{ width: 23 }}><FontAwesomeIcon icon={faUser} /></span>Traveller:</span>
                        <span className={'text-bold ph-5 ' + (order.order.paxes?.[0].fullDisplayName?.toLowerCase().includes('#t#') ? 'color-danger' : '')}>{order.order.paxes?.[0].fullDisplayName}</span>
                      </div>
                      <div className='cur-poi us-none' onDoubleClick={() => copyToClipboard(dateFormat(order.fullSegment.checkIn, 'dd/MM/yy'))}>
                        <span className='mw-92 display-ib'>
                          <span className='display-ib' style={{ width: 23 }}><FontAwesomeIcon icon={faCalendar} /></span>Check In:</span>
                        <span className='text-bold ph-5'>{dateFormat(order.fullSegment.checkIn, 'dd/MM/yy')}</span>
                      </div>
                      <div className='cur-poi us-none' onDoubleClick={() => copyToClipboard(order.fullSegment.backOfficeId)}>
                        <span className='mw-92 display-ib'>
                          <span className='display-ib' style={{ width: 23 }}><FontAwesomeIcon icon={faTicket} /></span>Docket:</span>
                        {order.fullSegment.backOfficeId !== '0' && order.fullSegment.backOfficeId !== 'NA' && <span className='text-bold ph-5'>{order.fullSegment.backOfficeId}</span>}
                      </div>
                    </div>
                    <div className='col-md-38 br-1'>
                      <div className="mb-15 h100p display-flex flex-v-start flex-gap-10">

                        <div className='flex-column h100p' >
                          {order.fullSegment.productType === 'HTL' && (
                            <>
                              {order.fullSegment.rooms.filter((room: any) => room.delRow === 0).map((room: any, index: number) => (
                                <div className="text-sm" key={index}>
                                  <div className='cur-poi us-none' onDoubleClick={() => copyToClipboard(room.roomDisplay)}>Room {index + 1}
                                    {room.paxes?.length > 0 && ` - ${room.paxes.length} ${room.paxes.length === 1 ? 'guest' : 'guests'}`}
                                    {room.sysStatus && (
                                      <span className='ph-5'>
                                        <span className={`mo-status ${getStatus(room.sysStatus.code)}`}>
                                          {room.sysStatus.code}
                                        </span>

                                      </span>
                                    )}</div>
                                  <div className='text-bold cur-poi us-none' onDoubleClick={() => copyToClipboard(room.roomDisplay)}>
                                    {room.roomDisplay}
                                  </div>
                                </div>
                              ))}
                            </>
                          )}

                          <div className='flex-row mt-a'>
                            <div
                              className='display-ib mr-10 position-relative'
                              ref={dropdownRef}
                              onClick={() => setHoveredOrderId(hoveredOrderId === order.fullSegment.orderSegId ? null : order.fullSegment.orderSegId)}
                            >
                              <FontAwesomeIcon
                                icon={faUser}
                                className="cur-poi"
                              />

                              <div className={'header-dropdown' + (hoveredOrderId === order.fullSegment.orderSegId ? ' open' : '')} style={{ left: 0, right: -250, top: 25 }}>
                                <div className="header-dropdown-row ph-10">
                                  <div className="ph-10 mt-10 mb-15 text-sm">
                                    <span className="mw-92">Creation Date - </span>
                                    <span className='text-bold'>{dateFormat(order.orderRow.dateC, 'dd/MM/yy HH:mm')}</span>
                                  </div>
                                  <div className="ph-10 mt-10 mb-15 text-sm">
                                    <span className="mw-92">{t('mo.user')}</span>: {order.user.user_code} -{' '}
                                    <span className='text-bold'>{order.user.fullName || `${order.user.user_f_name} ${order.user.user_l_name}`}</span>
                                  </div>
                                  <div className="ph-10 mt-10 mb-15 text-sm">
                                    <span className="mw-92">{t('mo.branch')}</span>: {order.orderRow.branchId} -{' '}
                                    <span className='text-bold'>{order.orderRow.branchName}</span>
                                  </div>
                                  <div className="ph-10 mt-10 mb-15 text-sm">
                                    <span className="mw-92">{t('mo.department')}</span>: {order.orderRow.departmentId} -{' '}
                                    <span className='text-bold'>{order.orderRow.departmentName}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <Button title="More Details" icon={faInfo} iconclassname="mr-5" className="btn btn-default btn-ssm mt-a w150px btn-blue" onClick={() => handleMoreDetailsClick(order)} />

                            {
                              order.fullSegment.countryCode && (
                                <div className='flex-center'>
                                  <span className='ml-10 mr-3'><ReactCountryFlag svg countryCode={order.fullSegment.countryCode} /></span>
                                  <span className='text-xsm position-relative' style={{ top: 2 }}>{order.fullSegment.countryCode}</span>
                                </div>
                              )
                            }

                            {(() => {
                              const appYes = order.fullSegment.remarks?.find((remark: any) => remark.remarkCode === 'APPYES');
                              const appAgt = order.fullSegment.remarks?.find((remark: any) => remark.remarkCode?.includes('AGT'));
                              const appOpn = order.fullSegment.remarks?.filter((remark: any) => remark.remarkCode === 'APPOPN') || [];
                              const appArt = order.fullSegment.remarks?.filter((remark: any) => remark.remarkCode === 'APPART') || [];
                              const isClosedAppOpn = appOpn.every((remark: any) => remark.remarkDesc?.includes('Closed'));
                              const isClosedAppArt = appArt.every((remark: any) => remark.remarkDesc?.includes('Closed'));

                              return (
                                <div onClick={() => handleRemarksClick(order)} className='flex-row cur-poi'>
                                  {appYes && (
                                    <div className='flex-center ml-10'>
                                      <FontAwesomeIcon
                                        icon={faPhoneSquare}
                                        color="var(--color-text)"
                                      />
                                    </div>
                                  )}
                                  {appAgt && (
                                    <div className='flex-center ml-10'>
                                      <FontAwesomeIcon
                                        icon={faUser}
                                        color="var(--color-text)"
                                      />
                                    </div>
                                  )}
                                  {appOpn.length > 0 && (
                                    <div className='flex-center ml-10'>
                                      <FontAwesomeIcon
                                        icon={faCheckSquare}
                                        color={isClosedAppOpn ? 'var(--color-text)' : 'var(--color-success)'}
                                      />
                                    </div>
                                  )}
                                  {appArt.length > 0 && (
                                    <div className='flex-center ml-10'>
                                      <FontAwesomeIcon
                                        icon={faExclamationCircle}
                                        color={isClosedAppArt ? 'var(--color-text)' : 'var(--color-success)'}
                                      />
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </div>

                        </div>
                      </div>
                    </div>
                    <div className='col-md-31 text-right'>
                      <div className='flex-column h100p'>
                        <div className='flex-between'>
                          <div>
                            {order.orderRow.paidStatus && (
                              <Tooltip content={order.orderRow.paidStatus}>
                                <span className={`payment ${getPayment(order.orderRow.paidStatus)}`}>
                                  <FontAwesomeIcon icon={faCcVisa as any} size='xl' className='mr-3 cur-poi' onClick={() => handlePaymentDetailsClick(order)} />
                                </span>
                              </Tooltip>
                            )}
                          </div>
                          <div className='text-sm cur-poi btn-blue' onClick={() => handlePriceDetailsClick(order)}>Price details & commission</div>

                        </div>
                        {order.fullSegment.rooms?.[0]?.cancellationDateLimit && new Date(order.fullSegment.rooms[0].cancellationDateLimit).getFullYear() >= 1900 && (
                          <div className={'text-sm mb-10 cur-poi ' + (new Date(order.fullSegment.rooms[0].cancellationDateLimit) > new Date()
                            ? 'text-success'
                            : 'color-danger')} onClick={() => handleCxlClick(order)}>

                            {new Date(order.fullSegment.rooms[0].cancellationDateLimit) > new Date() ? <span>
                              {'Cancallation starts at '}
                              <span className='text-bold'>{dateFormat(order.fullSegment.rooms[0].cancellationDateLimit, 'dd-MM-yyyy')}</span>
                            </span> : 'Non refundable'}

                            {order.fullSegment.rooms[0].cancellationTimeLimit && ` ${order.fullSegment.rooms[0].cancellationTimeLimit}`}
                          </div>
                        )}

                        <div className='mt-a flex-row' style={{ gap: '5px', flexDirection: 'row-reverse' }}>

                          <MoCancelModal orderRow={order.orderRow} onSuccess={props.onUpdateMyOrder} trashIcon={true} />
                          <MoStatus segment={order.fullSegment} orderRow={order.orderRow} onSuccess={props.onUpdateMyOrder} />
                          <MoChangeRequest segment={order.fullSegment} orderRow={order.orderRow} onSuccess={props.onUpdateMyOrder} />
                          <MoCog myOrder={order} onSuccess={props.onUpdateMyOrder} user={order.user} dropdownProps={{ appendToBody: !isMobile, dropdownClassName: "c-dropdown-concat", appendToDirection: "top" }} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </InfiniteScroll>
        </div>
      </div>

      {selectedOrder && (
        <MoDocumentsModal
          openModal={isDocumentsModalOpen}
          setOpenModal={setIsDocumentsModalOpen}
          myOrder={selectedOrder}
          onUpdateMyOrder={handleUpdateMyOrder}
        />
      )}
      {moreDetailsOrder && (
        <MoMoreDetailsModal
          openModal={isMoreDetailsModalOpen}
          setOpenModal={setIsMoreDetailsModalOpen}
          myOrder={moreDetailsOrder}
          onUpdateMyOrder={handleUpdateMyOrder}
          activeInitialTab={activeTab}
        />
      )}
    </Fragment>
  );
};

export default withErrorBoundary(MoMode2, 'MoMode2');
